import { observer } from 'mobx-react';
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
    MainScreen,
    OverContentLoadableBlock,
    PageFrame,
    useNavigate,
} from '@alfa-retail-react/components';

import { unauthorizedExceptionHandlerOnLoadData } from 'app/logic/pages/error/unauthorizedExceptionHandler';
import SessionStorageTokenStore from 'app/auth/SessionStorageTokenStore';
import { useAnalytics } from 'app/analytics/useAnalytics';
import { useApplicationContext } from 'app/store/сontext/ApplicationContext';
import { useNotificationContext } from 'app/store/сontext/NotificationContextProvider';
import { defaultExceptionHandler } from 'app/logic/pages/error/defaultExceptionHandler';
import {
    ERROR_PAGE_URL,
    ErrorPageState,
} from 'app/logic/pages/error/ErrorPage';
import { exceptionLogger } from 'app/logic/pages/error/exceptionLogger';
import { networkExceptionHandlerOnLoadData } from 'app/logic/pages/error/networkExceptionHandler';
import { notFoundExceptionHandler } from 'app/logic/pages/error/notFoundExceptionHandler';
import { DEFAULT_ORDER_STATE } from 'app/model/CardOrder';
import { PageNames } from 'app/PageNames';

import { FORM_ROUTES } from '../../../../app-routes';
import { ContinueDraftByLinkPageStore } from './ContinueDraftByLinkPageStore';

export const CONTINUE_DRAFT_BY_LINK_PAGE_URL = '/c';

/**
 * Расшифровка сокращений параметров в URL
 */
export const CONTINUE_PARAMS = {
    orderId: 'oid',
    internalId: 'iid',
    source: 'src',
    channel: 'c',
};

const PAGE_NAME = 'ContinueDraftByLinkPage';

export const ContinueDraftByLinkPage = observer(() => {
    const { search } = useLocation();
    const navigate = useNavigate();
    const applicationContext = useApplicationContext();
    const pageStore = useRef(new ContinueDraftByLinkPageStore()).current;
    const notification = useNotificationContext();

    const query = new URLSearchParams(search);
    const { orderId, source, channel, internalId } = {
        orderId: query.get(CONTINUE_PARAMS.orderId),
        source: query.get(CONTINUE_PARAMS.source),
        channel: query.get(CONTINUE_PARAMS.channel),
        internalId: query.get(CONTINUE_PARAMS.internalId),
    };

    const isInitialLoad = !applicationContext.isDraftContinuationByLink;

    if (isInitialLoad) {
        SessionStorageTokenStore.clear();
        sessionStorage.setItem('orderId', orderId);
        applicationContext.channel = channel;
        applicationContext.internalId = internalId;
        applicationContext.isDraftContinuationByLink = true;
        applicationContext.utmSource = source;
    }

    const analytics = useAnalytics('', PAGE_NAME, false);
    const notFoundErrorAnalytics = analytics.reject;
    const notFoundErrorState: ErrorPageState = {
        title: 'Не удалось найти заявку',
        messages: ['Пожалуйста, оформите новую заявку на кредитную карту'],
        actionButtonText: 'Оформить',
        actionHandler: () => {
            window.location.href = (window as any).env.REFERER_URL;
        },
    };

    const draftContinuationByLinkHandler = () => {
        const { orderToContinue, cardProduct } = pageStore;
        const isOrderValid =
            orderToContinue?.state === DEFAULT_ORDER_STATE &&
            orderToContinue?.number.toString() === internalId;

        if (isOrderValid) {
            applicationContext.cardProduct = cardProduct;
            applicationContext.phoneNumber = orderToContinue.client.mobilePhone;

            analytics.confirm({ productCode: cardProduct.code });

            navigate(
                `/${cardProduct.code}/${FORM_ROUTES.order}?orderId=${orderId}&channel=${applicationContext.channel}`,
                {
                    refererPage: PageNames.CONTINUE_DRAFT_PAGE_NAME,
                    isDraftContinuation: true,
                },
            );
        } else {
            notFoundErrorAnalytics();
            navigate(ERROR_PAGE_URL, notFoundErrorState);
        }
    };

    useEffect(() => {
        if (!channel || !orderId || !source || !internalId) {
            navigate(ERROR_PAGE_URL, notFoundErrorState);
            return;
        }

        if (isInitialLoad) {
            analytics.pageDisplayed();
        }

        pageStore
            .initialize({ orderId, channel: applicationContext.channel })
            .then(draftContinuationByLinkHandler)
            .catch(
                unauthorizedExceptionHandlerOnLoadData({
                    navigate,
                    phoneNumber: applicationContext.phoneNumber,
                }),
            )
            .catch(exceptionLogger(PAGE_NAME, 'pageStore.initialize'))
            .catch(networkExceptionHandlerOnLoadData({ navigate }))
            .catch(
                notFoundExceptionHandler(
                    { navigate, ...notFoundErrorState },
                    notFoundErrorAnalytics,
                ),
            )
            .catch(defaultExceptionHandler(notification, analytics));
    }, []);

    return (
        <OverContentLoadableBlock show>
            <PageFrame displayFooter>
                <MainScreen>
                    <MainScreen.Header
                        title={applicationContext.getCardProductTitle()}
                    />
                    <MainScreen.ContentBar>{null}</MainScreen.ContentBar>
                </MainScreen>
            </PageFrame>
        </OverContentLoadableBlock>
    );
});
